import React from "react";
import {
  Box,
  Divider,
  Heading,
  Hidden,
  Inline,
  Text,
  TileLink,
} from "@origin-digital/ods-core";
import {
  IconChevronRightSmall,
  IconDescription,
} from "@origin-digital/ods-icons";
import type { CategoryCardProps, SubLink } from "./types";
import { ICategoryDto as Category } from "../../contract";

const SubLinkItem = ({
  subLink,
  idPrefix,
}: {
  subLink: SubLink;
  idPrefix: string;
}) => (
  <React.Fragment>
    <TileLink
      href={subLink.href}
      dataId={`${idPrefix}-${
        subLink.href.split("/").filter(Boolean).pop() ??
        subLink.label.toLowerCase().replace(/\s+/g, "-")
      }`}
      trackingType="button"
      trackingLabel={`Article:${subLink.label}`}
      trackingData={{
        label: `Article:${subLink.label}`,
        componentType: "button",
      }}
      cardProps={{
        backgroundColor: "transparent",
        paddingXSpecific: "medium",
        paddingYSpecific: "medium",
      }}
      hideChevron
    >
      <Box
        alignItems="center"
        display="flex"
        width="full"
        justifyContent="space-between"
      >
        <Inline space="small" alignY="center">
          <IconDescription color="neutral" size="xxsmall" />
          <Text tone="neutral" weight="medium">
            {subLink.label}
          </Text>
        </Inline>
      </Box>
    </TileLink>
    <Box paddingX="small">
      <Divider />
    </Box>
  </React.Fragment>
);

export const SubLinkList = ({
  subLinks,
  idPrefix,
}: {
  subLinks: SubLink[];
  idPrefix: string;
}) => (
  <Box
    display="flex"
    width="full"
    flexDirection="column"
    paddingX={{ xs: "xsmall", sm: "medium" }}
    paddingTop="medium"
  >
    {subLinks.slice(0, 2).map((subLink) => (
      <SubLinkItem key={subLink.href} subLink={subLink} idPrefix={idPrefix} />
    ))}
  </Box>
);

const PictogramSection = ({ src }: { src: string }) => (
  <Box display="flex" width="full" marginBottom="small">
    <img src={src} width="96" height="96" alt="" />
  </Box>
);

const CategoryCardTitleRow = ({ name }: { name: string }) => (
  <Box
    display="flex"
    width="full"
    flexDirection="row"
    alignItems="center"
    justifyContent="space-between"
    gap="medium"
  >
    <Heading component="h3" variant="h2">
      {name}
    </Heading>
    <Box display="flex" position="relative" left="xxsmall">
      <IconChevronRightSmall
        variant="solid"
        size="xsmall"
        color="neutralDark"
      />
    </Box>
  </Box>
);

const CategoryCardHeader = ({
  name,
  description,
  icon,
  image,
}: {
  name: string;
  description: string;
  icon: string | null;
  image?: string | null;
}) => {
  return (
    <Box
      display="flex"
      width="full"
      flexDirection="column"
      paddingX={{ xs: "large", sm: "xlarge" }}
      height="full"
    >
      {/* Desktop (md+): show pictogram from image URL */}
      {image && (
        <Hidden below="lg">
          <PictogramSection src={image} />
        </Hidden>
      )}
      {/* Mobile (xs/sm): show icon from URL */}
      {icon && (
        <Hidden above="lg">
          <Box display="flex" width="full" marginBottom="small">
            <img src={icon} width="32" height="32" alt="" />
          </Box>
        </Hidden>
      )}
      <Box display="flex" width="full" flexDirection="column" gap="small">
        <CategoryCardTitleRow name={name} />
        <Hidden below="lg">
          <Text tone="neutralDark">{description}</Text>
        </Hidden>
      </Box>
    </Box>
  );
};

const CategoryCardBody = ({
  category,
  subLinks,
}: {
  category: Category;
  subLinks: SubLink[];
}) => (
  <Box display="flex" width="full" flexDirection="column" paddingY="xlarge">
    <CategoryCardHeader
      name={category.name}
      description={category.description}
      icon={category.icon}
      image={category.image}
    />
    {/*
     * Invisible phantom — preserves the card height so TileLink covers the full
     * card area (hover effect, focus outline). The real SubLinkList is rendered
     * outside TileLink's DOM so its onClickCapture (analytics) does not fire
     * when a sublink is clicked.
     */}
    <div
      aria-hidden="true"
      style={{ visibility: "hidden", pointerEvents: "none" }}
    >
      <SubLinkList subLinks={subLinks} idPrefix="phantom" />
    </div>
  </Box>
);

export const CategoryCard: React.FC<CategoryCardProps> = ({ category }) => {
  const subLinks = category.subLinks ?? [];
  return (
    <Box
      display="flex"
      height="full"
      width="full"
      position="relative"
      data-id={`category-card-${category.slug}`}
    >
      <TileLink
        href={category.url}
        hideChevron
        dataId={`category-tile-${category.slug}`}
        trackingType="button"
        trackingLabel={`Category:${category.name}`}
        trackingData={{
          label: `Category:${category.name}`,
          componentType: "button",
        }}
        cardProps={{
          borderRadius: "medium",
          backgroundColor: "grey50",
          paddingXSpecific: "none",
          paddingYSpecific: "none",
          height: "full",
        }}
      >
        <CategoryCardBody category={category} subLinks={subLinks} />
      </TileLink>
      {/* Real SubLinkList — sibling to TileLink so parent onClickCapture never fires */}
      <Box
        position="absolute"
        style={{ bottom: 0, left: 0, right: 0 }}
        paddingBottom="xlarge"
      >
        <SubLinkList
          subLinks={subLinks}
          idPrefix={`sub-link-${category.slug}`}
        />
      </Box>
    </Box>
  );
};
